import Button from '@allesauto/core/button';
import Checkbox from '@allesauto/core/checkbox';
import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import InputField from '@allesauto/core/inputField';
import successDarkAnimation from '@allesauto/design/animations/successDark.json';
import { FieldTypeHTML, useNewsletter } from '@allesauto/utils/apiHooks';
import { ToastifyType } from '@allesauto/utils/consts';
import { createMarkup } from '@allesauto/utils/funcs';
import { renderRichText } from '@storyblok/react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { useFormik } from 'formik';
import React, { FC, useCallback, useEffect, useRef } from 'react';

import ReCaptchaValidator, {
  ITokenValidator,
} from '@/components/ReCaptcha/ReCaptchaValidator';

import { DynamicAnimation } from '@/global/dynamicComponents/DynamicAnimation';

import validationSchema from '../validation/newsletterFormSchema';

interface FormValues {
  email: string;
  hasConsent: boolean;
}

export type NewsletterStandaloneFormProps = {
  headline: string;
  subheadline: string;
  sendHeadline: string;
  sendSubheadline: string;
  buttonLabel: string;
  inputPlaceholderText: string;
  inputError: string;
  privacyConsent: string;
  popupErrorHeadline: string;
  popupErrorText: string;
  component: string;
  _uid: string;
  _editable?: string;
};

const NewsletterStandaloneForm: FC<
  React.PropsWithChildren<NewsletterStandaloneFormProps>
> = (props) => {
  const {
    headline,
    sendHeadline,
    subheadline,
    sendSubheadline,
    buttonLabel,
    inputPlaceholderText,
    inputError,
    privacyConsent,
    popupErrorHeadline,
    popupErrorText,
  } = props;
  const [sendNewsletterRequest, { result: isSuccess, loading, error }] =
    useNewsletter();

  const tokenValidator = useRef<ITokenValidator | undefined>();
  const recaptchaToken = useRef<string | null>(null);

  const setTokenValidator = useCallback((v: ITokenValidator) => {
    tokenValidator.current = v;
  }, []);

  useEffect(() => {
    if (error) {
      import('@allesauto/core/toastify').then((module) => {
        module.toastifyFactory(
          ToastifyType.ERROR,
          popupErrorHeadline,
          popupErrorText
        );
      });
    }
  }, [error]);

  const handleSubmit = (values: FormValues) => {
    if (loading) {
      return;
    }

    sendNewsletterRequest({
      type: 'subscribe',
      email: values.email,
      source: 'SubscribeForm',
      recaptchaToken: recaptchaToken.current,
    });
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      email: '',
      hasConsent: false,
    },
    validationSchema: validationSchema({
      email: inputError,
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  const className = {
    wrapper: 'bg-purple80 p-30px lg:p-80px text-white',
    headline: 'mb-20px uppercase text-center break-words',
    description: 'max-w-lg text-center text-p1 mx-auto',
    form: 'max-w-3xl mt-30px lg:mt-60px lg:mb-30px mx-auto',
    inputWrapper: 'flex flex-col mb-40px lg:flex-row lg:mb-80px',
    input: clsx(
      'bg-white grow mb-15px rounded-default standalone-newsletter-input lg:mb-0 lg:mr-20px',
      formik.touched.email && formik.errors.email && 'sm:mb-30px'
    ),
    buttonWrapper: 'w-full lg:w-1/3',
    button: 'whitespace-nowrap w-full',
    privacyConsent: clsx(
      'standalone-newsletter-legal',
      !!(formik.touched.hasConsent && formik.errors.hasConsent) &&
        'newsletter-legal--error'
    ),
    recaptcha: 'standalone-newsletter-legal',
    success: 'mt-[55px] mb-40px',
  };

  const onNewsletterSignup = useCallback(async () => {
    if (process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA) {
      if (tokenValidator.current) {
        recaptchaToken.current = await tokenValidator.current.validate();
      }
      if (!recaptchaToken.current) {
        return;
      }
    }
    if (!loading) {
      formik.submitForm();
    }
  }, [formik, loading]);

  return (
    <div {...sbEditable(props)} className={className.wrapper}>
      <h1 className={className.headline}>
        {isSuccess ? sendHeadline : headline}
      </h1>
      <p className={className.description}>
        {isSuccess ? sendSubheadline : subheadline}
      </p>
      {isSuccess ? (
        <div className={className.success}>
          <DynamicAnimation
            width={160}
            height={160}
            autoplay={true}
            file={successDarkAnimation}
          />
        </div>
      ) : (
        <div className={className.form}>
          <div className={className.inputWrapper}>
            <InputField
              id="aa-newsletter--email"
              placeholder={inputPlaceholderText}
              name="email"
              value={formik.values.email}
              darkBg={true}
              onChange={formik.handleChange}
              errorMessage={formik.touched.email && formik.errors.email}
              customStyle={className.input}
            />

            <div className={className.buttonWrapper}>
              <Button
                onClick={onNewsletterSignup}
                customClassName={className.button}
              >
                {loading ? (
                  <CircularLoader
                    type={CircularLoaderType.primary}
                    customSize={CircularLoaderSize.sm}
                  />
                ) : (
                  buttonLabel
                )}
              </Button>
            </div>
          </div>

          <Checkbox
            id="aa-newsletter--gdpr"
            label={
              <div
                className={className.privacyConsent}
                dangerouslySetInnerHTML={createMarkup(
                  (privacyConsent as unknown as FieldTypeHTML).type
                    ? renderRichText(privacyConsent as unknown as FieldTypeHTML)
                    : privacyConsent
                )}
              ></div>
            }
            checked={formik.values.hasConsent}
            onChange={(value: boolean) =>
              formik.setFieldValue('hasConsent', value)
            }
            invalid={formik.touched.hasConsent && !!formik.errors.hasConsent}
            darkBg
          />
          {process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA && (
            <ReCaptchaValidator
              legalTextColor="white"
              legalLinkColor="white"
              setStepValidator={setTokenValidator}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterStandaloneForm;
